// What the workspace's tabs share: the props each tab gets from the page, and small selectors over the workspace.
import type { E2IConfig, EnquiryWorkspace, Estimate, Job, Quotation } from '../../types/e2i';
import { emptyE2IConfig } from '../../utils/e2i';

export type Notify = (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;

export type WorkspaceTab = 'enquiry' | 'estimate' | 'quotation' | 'job' | 'cirs' | 'history';
export const WORKSPACE_TABS: WorkspaceTab[] = ['enquiry', 'estimate', 'quotation', 'job', 'cirs', 'history'];

export interface WorkspaceTabProps {
  /** Moves to another tab (the next step). */
  goTo: (tab: WorkspaceTab) => void;
  ws: EnquiryWorkspace;
  config: E2IConfig;
  companyId: number;
  currency: string;
  /** Shows the workspace an action answered with. */
  onWorkspace: (ws: EnquiryWorkspace) => void;
  /** Reads the workspace again (after an action that answers with something else). */
  reload: () => Promise<void>;
  notify: Notify;
}

/** The highest revision first. */
export const byRevisionDesc = <T extends { revision: number }>(rows: T[]): T[] => [...rows].sort((a, b) => b.revision - a.revision);

export const latestEstimate = (ws: EnquiryWorkspace): Estimate | null => byRevisionDesc(ws.estimates || [])[0] || null;
export const latestQuotation = (ws: EnquiryWorkspace): Quotation | null => byRevisionDesc(ws.quotations || [])[0] || null;

/** The quotation the client approved (the base of a job from a quotation). */
export const clientApprovedQuotation = (ws: EnquiryWorkspace): Quotation | null =>
  byRevisionDesc(ws.quotations || []).find((q) => q.status === 'ClientApproved') || null;

/** Jobs that are not cancelled. */
export const liveJobs = (ws: EnquiryWorkspace): Job[] => (ws.jobs || []).filter((j) => j.status !== 'Cancelled');

export const quotationOfJob = (ws: EnquiryWorkspace, job: Job | null | undefined): Quotation | null =>
  job?.quotationId ? (ws.quotations || []).find((q) => q.quotationId === job.quotationId) || null : null;

/** The VAT rate (%) of a code, from the company's output VAT codes. */
export const vatRateOf = (config: E2IConfig | null | undefined, code: string | null | undefined, fallback = 0): number => {
  if (!code) return fallback;
  const c = config?.vatCodes?.find((v) => v.code === code);
  return c ? Number(c.rate) || 0 : fallback;
};

/** A configuration to show the workspace with when the company's own cannot be read (e.g. an approver without the module). */
export const fallbackConfig = (companyId: number): E2IConfig => emptyE2IConfig(companyId);
